import { LicenseManager } from '@ag-grid-enterprise/core';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { HttpService } from '@ats/api-sdk';
import { AuthService } from '@auth0/auth0-angular';
import { TranslocoService } from '@ngneat/transloco';
import { CookieService } from 'ngx-cookie-service';
import { productFruits } from 'product-fruits';
import {
  Subject,
  combineLatest,
  filter,
  forkJoin,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { UserInfoService } from 'src/app/core/services/user-info.service';
import { locales } from './core/constants/Locales';
import { CurrentUser, UserRoleType } from './core/models/user.model';
import { CustomWindow } from './core/models/window.model';
import { AppConfigService } from './core/services/app-config.service';
import { BannerService } from './core/services/banner.service';
import { DrawerService } from './core/services/drawer.service';
import { FlagService } from './core/services/flag.service';
import { HierarchyService } from './core/services/hierarchy.service';
import { IntegrationsService } from './core/services/integrations.service';
import { LoggerService } from './core/services/logger.service';
import { NotificationService } from './core/services/notification.service';
import { PopupToasterService } from './core/services/popup-toaster.service';
import { TenantService } from './core/services/tenant.service';
import { UsersService } from './core/services/users.service';
import { NotificationsWelcomeModalComponent } from './shared/notifications-welcome-modal/notifications-welcome-modal.component';

declare let newrelic: any;
declare let window: CustomWindow;

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass']
})
export class AppComponent implements OnInit, OnDestroy {
  loading = true;
  languageLoaded = false;
  bannerExpanded = false;

  private ngUnsubscribe = new Subject<void>();

  constructor(
    public drawerService: DrawerService,
    public auth: AuthService,
    private logger: LoggerService,
    private notificationService: NotificationService,
    public userInfoService: UserInfoService,
    private tenantService: TenantService,
    private usersService: UsersService,
    private cookieService: CookieService,
    private hierarchyService: HierarchyService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toasterService: PopupToasterService,
    public flagService: FlagService,
    public bannerService: BannerService,
    private translate: TranslocoService,
    private integrationsService: IntegrationsService,
    private dialog: MatDialog,
    private httpService: HttpService,
    private appConfigService: AppConfigService
  ) {
    this.httpService.config = {
      apiHost: this.appConfigService.mhmApiHost
    };
  }

  @ViewChild('navDrawer') set navContent(content: MatSidenav) {
    if (content) {
      this.drawerService.setSidebarNavDrawer(content);
    }
  }

  ngOnInit(): void {
    // Prevent user from being stuck at /auth_error if no auth observable has emitted
    if (window.location.pathname.includes('/auth_error')) {
      this.router.navigateByUrl('/');
    }

    let siteId = '';
    let roles: UserRoleType[] = [UserRoleType.Default];
    let internal = false;
    const claimBase = 'https://advancedtech.com';
    const cookieName = 'mhm_auth_org_id';
    const claims = {
      siteId: `${claimBase}/siteid`,
      roles: `${claimBase}/roles`,
      internal: `${claimBase}/internal`
    };

    LicenseManager.setLicenseKey(
      'Using_this_{AG_Grid}_Enterprise_key_{AG-073897}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ATS}_is_granted_a_{Multiple_Applications}_Developer_License_for_{3}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{4}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{9_February_2026}____[v3]_[01]_MTc3MDU5NTIwMDAwMA==70e03e42515650d414549d474145cb20'
    );

    this.auth.error$
      .pipe(
        filter((error) => !!error),
        tap(() => {
          // If error redirect to unguarded component to prevent login loop
          this.router.navigateByUrl('auth_error');
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap(() => (this.bannerExpanded = false))
      )
      .subscribe();

    combineLatest([this.auth.user$, this.auth.isAuthenticated$])
      .pipe(
        filter(([user, isAuthenticated]) => !!user && isAuthenticated),
        take(1),
        tap(([user]) => {
          internal = user![claims.internal] as boolean;

          if (user!['org_id']) {
            const cookieId = this.cookieService.get(cookieName);
            if (!cookieId.length || cookieId !== user!['org_id']) {
              this.cookieService.delete(cookieName);
              this.cookieService.set(cookieName, user!['org_id'], 2);
            }
            this.cookieService.delete(cookieName);
            this.cookieService.set(cookieName, user!['org_id'], 2);
          }

          if (user![claims.siteId]) {
            siteId = user![claims.siteId] as string;
          }

          if (user![claims.roles]) {
            roles = user![claims.roles] as UserRoleType[];
          }

          if (user!.picture) {
            this.userInfoService.setUserImg(user!.picture);
          }
        }),
        switchMap(() =>
          forkJoin([
            this.usersService.getCurrentUser(),
            this.integrationsService.getUserWebhooks(),
            this.hierarchyService.fetchHierarchy(),
            this.usersService.fetchBasicCompanyUsers()
          ])
        ),
        tap(([currentUser]) => {
          const company = this.hierarchyService.getCompany();

          if (company.regions.length > 1) {
            this.hierarchyService.setHasAssignedRegion(true);
          }

          const selectedTenant = currentUser.tenants.find(
            (tenant) => tenant.companyId === company.id
          );

          if (selectedTenant) {
            this.tenantService.setSelectedTenant(selectedTenant);
          }

          if (siteId) {
            const selectedSite = company.regions
              .flatMap((region) => region.sites)
              .find((site) => site.id === siteId);

            if (selectedSite) {
              this.hierarchyService.setSelectedSite(selectedSite);

              const selectedRegion = company.regions.find(
                (region) => region.id === selectedSite.regionId
              );

              if (selectedRegion) {
                this.hierarchyService.setSelectedRegion(selectedRegion);
              }
            }
          }
        }),
        tap(([currentUser, webhooks]) => {
          currentUser.roles = roles;
          currentUser.isInternal = internal;

          if (
            !(
              currentUser!.displayPreferences?.[
                'bypassnotificationwelcomeprompt'
              ] || webhooks?.length > 0
            )
          ) {
            this.showNotificationModal();
          }

          this.userInfoService.setInfo(currentUser);
          this.initLanguage(currentUser.language);
          this.initProductFruits(currentUser);
          this.initNewRelic(currentUser.id);
          this.flagService.initFlagsmith(currentUser.email);
          this.loading = false;
        }),
        switchMap(() => this.siteChangedListener()),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  showNotificationModal(): void {
    this.dialog.open(NotificationsWelcomeModalComponent, {
      width: '46rem',
      maxWidth: '46rem',
      maxHeight: '90vh'
    });
  }

  initProductFruits(user: CurrentUser) {
    productFruits.init(
      'bT20FHKHAgkVvvV4',
      user.language?.split('-')?.[0].toLowerCase() ?? 'en',
      {
        username: user.email
      }
    );
  }

  initNewRelic(personId: string) {
    window['newRelic']?.setCustomAttribute('user', personId);
  }

  initLanguage(language: string) {
    const firstUrlPathSegment = window.location.pathname.split('/')[1];
    let currentLocale = '';

    // Array of valid localeCodes
    if (locales.indexOf(firstUrlPathSegment) > -1) {
      currentLocale = firstUrlPathSegment;
    }

    if (language.toLowerCase().substring(0, 2) === 'en') {
      language = 'en-US';
    } else if (language.toLowerCase().substring(0, 2) === 'es') {
      language = 'es-MX';
    }

    this.translate.setDefaultLang(language.replace('-', '_'));

    language = language.toLowerCase();

    if (language && currentLocale && language !== currentLocale) {
      const redirectUrl = window.location.href.replace(
        `/${currentLocale}`,
        `/${language}`
      );
      window.location.href = redirectUrl;
    } else {
      this.languageLoaded = true;
    }
  }

  siteChangedListener() {
    return this.activatedRoute.queryParams.pipe(
      tap((params) => {
        if (params['siteChanged'] === 'true') {
          const selectedSite = this.hierarchyService.getSelectedSite();
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { siteChanged: null },
            queryParamsHandling: 'merge'
          });
          this.toasterService.success(
            $localize`Success`,
            $localize`Site has been changed to` + ` ${selectedSite.name}.`
          );
        }
      })
    );
  }

  closePauseAlertingBanner() {
    this.bannerService.setAlertingPausedItem(null);
  }
}
